"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Clock, PhoneCall, ShieldCheck, Wrench, Navigation, CircleCheck as CheckCircle2, ArrowRight, CircleAlert as AlertCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const neighborhoods = [{
  name: "Downtown Jonesboro & Historic District",
  desc: "Rapid response near Main St, Courthouse square, and surrounding heritage residential neighborhoods.",
  responseTime: "15-30 mins"
}, {
  name: "Lake Spivey & North Jonesboro",
  desc: "Complete coverage for lakeside properties, water pressure fixes, and emergency backup lines.",
  responseTime: "20-35 mins"
}, {
  name: "Tara Boulevard Commercial & Residential",
  desc: "Dedicated van units patrolling the main highway corridor for urgent business and residential leaks.",
  responseTime: "15-25 mins"
}, {
  name: "Mundy's Mill & South Clayton County",
  desc: "Fast service for subdivisions, water heater replacements, and sudden burst pipe repairs.",
  responseTime: "20-40 mins"
}, {
  name: "Irondale & Flint River District",
  desc: "Equipped for sewer lateral clearing, root intrusion emergencies, and heavy storm backups.",
  responseTime: "25-40 mins"
}, {
  name: "Lovejoy & Surrounding Metro Border",
  desc: "Extended perimeter dispatch with identical 24/7 rates and zero hidden travel surcharges.",
  responseTime: "25-45 mins"
}];
const zipCodes = ["30236", "30237", "30238", "30250", "30274", "30297"];
const localGuarantees = [{
  title: "Based Right in Jonesboro",
  desc: "We do not dispatch from across Atlanta. Our local plumbers are already in Clayton County.",
  icon: MapPin
}, {
  title: "Always Open 24/7/365",
  desc: "Midnight burst pipes, holiday water heater failures, and Sunday sewer emergencies handled on call.",
  icon: Clock
}, {
  title: "Fully Stocked Mobile Trucks",
  desc: "Over 90% of emergency repairs resolved on the initial visit without waiting on parts stores.",
  icon: Wrench
}, {
  title: "Clear, Upfront Pricing",
  desc: "Flat-rate quotes provided before we begin any work. No surprise extra fees or hidden charges.",
  icon: ShieldCheck
}];
export default function JonesboroArea() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="serviceareajonesboro" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5">
              <Navigation className="size-3.5 text-primary" />
              Jonesboro, GA Local Coverage
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-4">
            Emergency Plumbing Coverage in Jonesboro & Clayton County
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.2
        }} className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When a burst pipe, severe drain clog, or overflowing water heater threatens your property, our localized service trucks arrive promptly across every Jonesboro neighborhood.
          </motion.p>
        </div>

        {/* Dispatch Overview & Zip Codes Bar */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-muted/60 border border-border rounded-2xl p-6 sm:p-8 mb-12 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-7 space-y-3">
              <div className="flex items-center gap-2">
                <span className="flex size-3 rounded-full bg-accent animate-pulse" />
                <span className="text-xs font-bold uppercase tracking-wider text-primary">
                  Live Dispatch Status: Active in Jonesboro
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl font-default font-bold text-foreground">
                Serving All Primary Jonesboro Zip Codes
              </h3>
              <p className="text-sm text-muted-foreground">
                We provide immediate dispatch coverage across the entire 30236, 30237, and 30238 sectors, alongside adjacent South Metro communities.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                {zipCodes.map((zip, i) => <div key={zip} data-index={i} className="inline-flex items-center px-3 py-1 rounded-md bg-card border border-border text-sm font-semibold text-foreground shadow-2xs">
                    <MapPin className="size-3.5 mr-1 text-primary" />
                    ZIP {zip}
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end">
              <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
                <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <PhoneCall className="size-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Immediate Emergency Line</p>
                  <a href="tel:4708479076" className="text-base font-bold text-foreground hover:text-primary transition-colors block">
                    (470) 847-9076
                  </a>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-4">
                <div className="size-11 rounded-lg bg-accent/20 flex items-center justify-center shrink-0">
                  <Clock className="size-5 text-accent-foreground" />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">Average On-Site ETA</p>
                  <p className="text-base font-bold text-foreground">30-45 Minutes in Jonesboro</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Neighborhood Grid */}
        <div className="mb-14">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <h3 className="text-2xl font-default font-bold text-foreground tracking-tight">
                Neighborhoods We Patrol & Service Daily
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Fast emergency dispatch tailored specifically to local infrastructure and plumbing systems.
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="self-start sm:self-auto">
              <Link to="/service-areas/">
                View All Clayton Areas
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {neighborhoods.map((zone, i) => <div key={zone.name} data-index={i}>
                <Card className="h-full border border-border hover:border-primary/40 transition-[border-color,box-shadow] duration-200 shadow-xs hover:shadow-md bg-card">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <CardTitle className="text-lg font-default font-semibold text-card-foreground">
                        {zone.name}
                      </CardTitle>
                      <Badge variant="secondary" className="text-2xs font-semibold py-0.5 px-2 shrink-0">
                        {zone.responseTime}
                      </Badge>
                    </div>
                    <CardDescription className="text-sm text-muted-foreground leading-normal">
                      {zone.desc}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center gap-2 text-xs text-primary font-medium">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      <span>24/7 Master Plumber on Dispatch</span>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Local Advantage Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
          {localGuarantees.map((item, i) => {
          const IconComp = item.icon;
          return <div key={item.title} data-index={i} className="bg-card border border-border rounded-xl p-6 shadow-xs flex flex-col justify-between">
                <div>
                  <div className="size-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center mb-4">
                    <IconComp className="size-6" />
                  </div>
                  <h4 className="text-base font-default font-bold text-card-foreground mb-2">
                    {item.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Urgent Local Action Banner */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-8 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-foreground/15 text-xs font-semibold uppercase tracking-wider mb-4">
              <AlertCircle className="size-3.5" />
              Active Water Leak or Sewer Problem?
            </div>
            <h3 className="text-2xl sm:text-3xl font-default font-bold tracking-tight text-primary-foreground mb-3">
              Need a Local Jonesboro Plumber Right Away?
            </h3>
            <p className="text-sm sm:text-base text-primary-foreground/90 mb-6 leading-relaxed">
              Don't wait for minor leaks to become structural damage. Our licensed technicians are on standby throughout Jonesboro to secure your plumbing system now.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
                <a href="tel:4708479076">
                  <PhoneCall className="size-4 mr-2" />
                  Call (470) 847-9076 Now
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="bg-primary/20 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-medium">
                <Link to="/contact/">
                  Request Online Dispatch
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}